import {
    ChangeDetectorRef, Directive,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output
} from "@angular/core";
import {FUIAbstractInput} from "../../framework/abstract-input";
import {FUIOptionsService, SelectionEvent} from "../../framework/options.service";
import {Subscription} from "rxjs";
import {FUIEventsService} from "../../../events/events.service";

@Directive()
export abstract class FUIAbstractAutocompleteInput extends FUIAbstractInput implements OnDestroy, OnInit {
    @Input() public field: string;
    @Output() public filtered$: EventEmitter<string> = new EventEmitter<string>();
    @Input() public options: Array<any>;
    private _$click: Subscription;

    constructor(changeDetectorRef: ChangeDetectorRef,
                public elementRef: ElementRef,
                protected eventsService: FUIEventsService,
                protected optionsService: FUIOptionsService) {
        super(changeDetectorRef);
        this.popped = false;
    }

    private _key: string;

    /**
     * The labelfield used within the model
     * @returns {string}
     */
    get key(): string {
        return this._key;
    }

    /**
     * The labelfield used within the model
     * @param {string} key
     */
    @Input()
    set key(key: string) {
        if (this._key !== key) {
            this._key = key;
        }
    }

    private _popped: boolean;

    /**
     * Used to notify the popup of the
     * desired state
     * @returns {boolean}
     */
    get popped(): boolean {
        return this._popped;
    }

    /**
     * Used to notify the popup of the
     * desired state
     * @param {boolean} popped
     */
    set popped(popped: boolean) {
        if (this._popped !== popped) {
            this._popped = popped;
        }
    }

    private _query: string;

    /**
     * The search query used to filter
     * @returns {string}
     */
    get query(): string {
        return this._query;
    }

    /**
     * The search query used to filter
     * @param {string} query
     */
    set query(query: string) {
        if (this._query !== query) {
            this._query = query;
            this.filtered$.emit(this._query);

            if (this._query) {
                if (this.focussed) {
                    if (this.value) {
                        if (this._query != this.value[this.key]) {
                            this.value = null;
                        }
                    }
                    this.popped = true;
                }
            }
            else {
                this.popped = false;
            }
        }
    }

    /**
     * The model value
     * @returns {string}
     */
    override get value(): any {
        return this._value;
    }

    /**
     * The model value
     * @param value
     */
    @Input()
    override set value(value: any) {
        if (value !== this._value) {
            if (value === undefined) {
                value = null;
            }
            this._value = value;
            this.onChange(value);
            this.optionsService.selection = this._value;
            this.valueChange.emit(value);
            if (this.value) {
                this.query = this.value[this.key];
            }
        }
    }

    ngOnInit(): void {
        this._$click = this.eventsService.click$.subscribe(
            (click) => {
                if (this.popped && !this.elementRef.nativeElement.contains(click.target)) {
                    this.popped = false;
                }
            }
        )
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this._$click.unsubscribe();
    }

    /**
     * Perform events after a click on the host
     * @param element
     */
    @HostListener("click")
    public override click(): void {
        this.focus();
    }

    /**
     * Set focus to the element and check if the popup should
     * show
     * @param element
     */
    @HostListener("focus")
    public override focus(): void {
        if (this.input && !this.disabled && !this.focussed) {
            this.input.nativeElement.focus();

            if (this.query) {
                if (this.focussed) {
                    this.popped = true;
                }
            } else {
                this.popped = false;
            }
        }
    }

    /**
     * Check if the list of options is empty
     * @returns {boolean}
     */
    public empty(options: Array<any>): boolean {
        return !this.loading && (!options || options.length === 0);
    }

    /**
     * Check if the list of options is empty
     * @returns {boolean}
     */
    public filled(options: Array<any>): boolean {
        return options && options.length > 0;
    }

    /**
     * Toggle the popped value
     */
    public pop(): void {
        this.popped = !this.popped;
    }

    /**
     * If the user presses the enter key,
     * select the first option in the list and set it
     * as the value and query, then close the popup.
     * @param {KeyboardEvent} event
     */
    public keypress(keyboardEvent: KeyboardEvent): void {
        if (keyboardEvent.code === "Enter" || keyboardEvent.key === "Enter") {
            if (this.options) {
                this.value = this.options[0];
                this.popped = false;
                keyboardEvent.preventDefault();
                keyboardEvent.stopPropagation();
            }
        }
    }

    /**
     * Assign the selected value, and close the popup if the selection was made by the user
     * @param event
     */
    public select(event: SelectionEvent): void {
        this.value = event.selection;
        if (event.userSelection) {
            this.popped = false;
        }
    }

    /**
     * Initial write of a set value from
     * the model
     * @param value
     */
    public override writeValue(value: any): void {
        this._value = value;

        if (this._value) {
            this.optionsService.selection = this._value;
            this.query = this._value[this.key];
            this.valued = true;
        }
        else {
            this.valued = false;
            this.query = undefined;
        }
        this.changeDetectorRef.detectChanges();
    }

    override onBlur(event?: any): void {
        super.onBlur(event);

        setTimeout(() => {
            if (this.query && this.options && this.options.length === 1) {
                if (this.query === this.options[0][this.key]) {
                    this.select(new SelectionEvent(this.options[0], true));
                }
            }
        }, 500);
    }
}
